import type { HTMLAttributes, ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import classNames from 'classnames';


import { Box } from 'ui/box';

import Tooltip from 'components/partials/tooltip/tooltip';

import { clamp } from 'helpers/number.helpers';

export const TOKEN_USAGE_ERROR_TOOLTIP =
  'There was an issue requesting token details. You can still keep chatting.';
export const TOKEN_USAGE_UNAVAILABLE_LABEL = 'Token details unavailable';

const GOOD_THRESHOLD = 65;
const MEDIUM_THRESHOLD = 35;
const MIN_PERCENT = 0;
const MAX_PERCENT = 100;
const EMPTY_BAR_PERCENT = 0;

const CONTAINER_CLASS =
  'flex w-full flex-col items-start gap-0.5 rounded-md bg-white';
const BAR_SX = {
  height: '0.25rem',
  width: '100%',
  overflow: 'hidden',
  borderRadius: '0.125rem',
  bgcolor: 'tokens.gray.200',
} satisfies SxProps<Theme>;

const getUsageColorClass = (percentRemaining: number): string => {
  if (percentRemaining >= GOOD_THRESHOLD) {
    return 'bg-success-600';
  }

  if (percentRemaining >= MEDIUM_THRESHOLD) {
    return 'bg-amber-400';
  }

  return 'bg-critical-500';
};

export interface TokenUsageIndicatorProps {
  percentRemaining: number;
  resetDate: string;
  hasError?: boolean;
  isLoading?: boolean;
}

const TokenUsageIndicator = ({
  percentRemaining,
  resetDate,
  hasError = false,
  isLoading = false,
}: TokenUsageIndicatorProps) => {
  if (isLoading) {
    return (
      <div
        data-testid="token-usage-skeleton"
        className={classNames(CONTAINER_CLASS, 'animate-pulse')}
        aria-hidden="true"
      >
        <Box
          sx={{
            height: '1rem',
            width: '9rem',
            borderRadius: '0.25rem',
            bgcolor: 'tokens.gray.200',
          }}
        />
        <Box sx={BAR_SX} />
      </div>
    );
  }

  const clampedPercent = clamp(percentRemaining, MIN_PERCENT, MAX_PERCENT);

  const fillPercent = hasError ? EMPTY_BAR_PERCENT : clampedPercent;

  const tooltipContent: ReactNode = hasError ? (
    TOKEN_USAGE_ERROR_TOOLTIP
  ) : (
    <span>
      You have <span className="font-bold">{clampedPercent}%</span> of your
      monthly tokens available, allocation resets on{' '}
      <span className="font-bold">{resetDate}</span>.
    </span>
  );

  const barA11yProps: HTMLAttributes<HTMLDivElement> = hasError
    ? {
        role: 'img',
        'aria-label': `${TOKEN_USAGE_UNAVAILABLE_LABEL}. ${TOKEN_USAGE_ERROR_TOOLTIP}`,
      }
    : {
        role: 'progressbar',
        'aria-valuenow': clampedPercent,
        'aria-valuemin': MIN_PERCENT,
        'aria-valuemax': MAX_PERCENT,
        'aria-label': 'Token usage remaining',
        'aria-valuetext': `${clampedPercent}% tokens remaining, allocation resets on ${resetDate}.`,
      };

  return (
    <Tooltip
      content={tooltipContent}
      position="right-start"
      positionStrategy="fixed"
      customClass="!text-xs !max-w-40"
    >
      <div className={CONTAINER_CLASS}>
        <p className="font-semibold text-gray-700 whitespace-nowrap">
          {hasError
            ? TOKEN_USAGE_UNAVAILABLE_LABEL
            : `${clampedPercent}% tokens remaining`}
        </p>

        <Box {...barA11yProps} sx={BAR_SX}>
          <div
            data-testid="token-usage-fill"
            className={classNames('h-full', getUsageColorClass(clampedPercent))}
            style={{ width: `${fillPercent}%` }}
          />
        </Box>
      </div>
    </Tooltip>
  );
};

export default TokenUsageIndicator;
